'use client';

// ─── Types ────────────────────────────────────────────────────────────────────

export type RequisitionStatus =
  | 'draft' | 'submitted' | 'pending_approval' | 'approved' | 'partially_approved' |'rejected' | 'returned' | 'converted_rfq' | 'converted_po' | 'partially_procured'
  | 'completed' | 'cancelled';

export type POStatus =
  | 'draft' | 'pending_approval' | 'approved' | 'issued' | 'partially_delivered' |'fully_delivered' | 'closed' | 'cancelled';

export type GRNStatus = 'draft' | 'pending_inspection' | 'approved' | 'rejected' | 'partial';

export type InvoiceStatus = 'pending' | 'matched' | 'minor_variance' | 'mismatch' | 'approved' | 'paid' | 'overdue';

export type RFQStatus = 'draft' | 'issued' | 'open' | 'closed' | 'evaluated' | 'cancelled';

export type QuotationStatus = 'received' | 'under_review' | 'shortlisted' | 'accepted' | 'rejected';

export type Priority = 'low' | 'normal' | 'high' | 'urgent' | 'emergency';

export type ProcurementMethod =
  | 'rfq' | 'direct' | 'competitive_bidding' | 'single_source' | 'emergency' | 'framework' | 'petty';

export type ApprovalAction = 'approved' | 'rejected' | 'returned' | 'escalated' | 'delegated';

export interface PurchaseRequisitionItem {
  id: string;
  itemId?: string;
  description: string;
  category: string;
  unit: string;
  quantity: number;
  estimatedUnitCost: number;
  estimatedTotal: number;
  preferredSupplier?: string;
  specifications?: string;
  currentStock?: number;
}

export interface ApprovalRecord {
  id: string;
  level: number;
  approverName: string;
  approverRole: string;
  action: ApprovalAction | 'pending';
  comments?: string;
  date?: string;
  time?: string;
}

export interface PurchaseRequisition {
  id: string;
  requisitionNumber: string;
  requestDate: string;
  requiredByDate: string;
  farm: string;
  branch?: string;
  department: string;
  requestedBy: string;
  priority: Priority;
  procurementMethod: ProcurementMethod;
  purpose: string;
  budgetCode: string;
  costCentre: string;
  estimatedTotal: number;
  currency: string;
  status: RequisitionStatus;
  items: PurchaseRequisitionItem[];
  approvals: ApprovalRecord[];
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface PurchaseOrderItem {
  id: string;
  description: string;
  category: string;
  unit: string;
  quantity: number;
  unitPrice: number;
  tax: number;
  discount: number;
  lineTotal: number;
}

export interface PurchaseOrder {
  id: string;
  poNumber: string;
  purchaseDate: string;
  supplier: string;
  supplierCode: string;
  farm: string;
  department: string;
  requisitionId?: string;
  requisitionNumber?: string;
  buyer: string;
  currency: string;
  paymentTerms: string;
  deliveryTerms: string;
  expectedDeliveryDate: string;
  deliveryAddress: string;
  subtotal: number;
  discount: number;
  tax: number;
  deliveryCharges: number;
  grandTotal: number;
  status: POStatus;
  items: PurchaseOrderItem[];
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface GRNItem {
  id: string;
  description: string;
  unit: string;
  orderedQty: number;
  previouslyReceived: number;
  deliveredQty: number;
  acceptedQty: number;
  rejectedQty: number;
  damagedQty: number;
  batchNumber?: string;
  expiryDate?: string;
  remarks?: string;
}

export interface GoodsReceivedNote {
  id: string;
  grnNumber: string;
  poNumber: string;
  poId: string;
  supplier: string;
  deliveryNoteNumber: string;
  receiptDate: string;
  farm: string;
  store: string;
  receivedBy: string;
  inspectedBy: string;
  vehicleRef?: string;
  status: GRNStatus;
  items: GRNItem[];
  notes?: string;
  createdAt: string;
}

export interface SupplierInvoice {
  id: string;
  invoiceNumber: string;
  supplier: string;
  poNumber: string;
  poId: string;
  grnNumber: string;
  invoiceDate: string;
  dueDate: string;
  currency: string;
  subtotal: number;
  tax: number;
  discount: number;
  total: number;
  status: InvoiceStatus;
  matchStatus: 'matched' | 'minor_variance' | 'mismatch' | 'pending';
  paymentStatus: 'unpaid' | 'partially_paid' | 'paid' | 'overdue';
  notes?: string;
  createdAt: string;
}

export interface ProcurementAlert {
  id: string;
  type: 'approval_needed' | 'rfq_deadline' | 'delivery_overdue' | 'invoice_mismatch' | 'budget_exceeded' | 'goods_rejected';
  title: string;
  description: string;
  severity: 'info' | 'warning' | 'danger';
  date: string;
  actionUrl?: string;
}

// ─── RFQ Types ────────────────────────────────────────────────────────────────

export interface RFQItem {
  id: string;
  description: string;
  specifications?: string;
  category: string;
  unit: string;
  quantity: number;
  requiredDeliveryDate?: string;
}

export interface RFQSupplier {
  supplierId: string;
  supplierName: string;
  supplierCode: string;
  sentDate?: string;
  responseStatus: 'pending' | 'responded' | 'declined' | 'no_response';
}

export interface RFQ {
  id: string;
  rfqNumber: string;
  rfqDate: string;
  submissionDeadline: string;
  farm: string;
  requisitionId?: string;
  requisitionNumber?: string;
  procurementOfficer: string;
  currency: string;
  deliveryLocation: string;
  paymentTerms: string;
  deliveryTerms: string;
  notes?: string;
  status: RFQStatus;
  suppliers: RFQSupplier[];
  items: RFQItem[];
  createdAt: string;
  updatedAt: string;
}

// ─── Quotation Types ──────────────────────────────────────────────────────────

export interface QuotationItem {
  id: string;
  description: string;
  unit: string;
  quantity: number;
  unitPrice: number;
  discount: number;
  tax: number;
  totalPrice: number;
  deliveryTime?: string;
  brand?: string;
  specificationsCompliance: 'full' | 'partial' | 'non_compliant';
}

export interface Quotation {
  id: string;
  quotationNumber: string;
  supplierId: string;
  supplierName: string;
  rfqId: string;
  rfqNumber: string;
  quotationDate: string;
  validityDate: string;
  currency: string;
  paymentTerms: string;
  deliveryPeriod: string;
  deliveryCharges: number;
  tax: number;
  discount: number;
  subtotal: number;
  grandTotal: number;
  warranty?: string;
  notes?: string;
  status: QuotationStatus;
  items: QuotationItem[];
  createdAt: string;
  updatedAt: string;
}

// ─── Mock Data ────────────────────────────────────────────────────────────────

export const FARMS_LIST = ['Idudi Poultry Farm', 'Jinja Branch', 'Kampala Branch'];
export const DEPARTMENTS = ['Feed Management', 'Health & Veterinary', 'Farm Operations', 'Administration', 'Finance', 'Maintenance'];
export const PROCUREMENT_CATEGORIES = [
  'Feed', 'Day-Old Chicks', 'Vaccines', 'Medication', 'Disinfectants', 'Chemicals',
  'Equipment', 'Spare Parts', 'Fuel', 'Egg Trays', 'Packaging', 'PPE',
  'Cleaning Materials', 'Construction Materials', 'Office Supplies', 'Veterinary Services',
  'Transport Services', 'Maintenance Services', 'Other',
];
export const UNITS = ['kg', 'bag', 'tonne', 'litre', 'piece', 'box', 'carton', 'set', 'pair', 'roll'];
export const SUPPLIERS = [
  { id: 'sup-001', name: 'Ugachick Poultry Breeders', code: 'UPB-001', category: 'Feed', status: 'active', rating: 4.5 },
  { id: 'sup-002', name: 'Nile Agro Supplies Ltd', code: 'NAS-002', category: 'Feed', status: 'active', rating: 4.2 },
  { id: 'sup-003', name: 'Kampala Vet Supplies', code: 'KVS-003', category: 'Vaccines', status: 'active', rating: 4.7 },
  { id: 'sup-004', name: 'East Africa Agri-Inputs', code: 'EAA-004', category: 'Chemicals', status: 'active', rating: 3.9 },
  { id: 'sup-005', name: 'Jinja Farm Supplies', code: 'JFS-005', category: 'Equipment', status: 'active', rating: 4.1 },
];

export const MOCK_REQUISITIONS: PurchaseRequisition[] = [
  {
    id: 'req-001',
    requisitionNumber: 'PR-2026-0045',
    requestDate: '2026-09-01',
    requiredByDate: '2026-09-10',
    farm: 'Idudi Poultry Farm',
    department: 'Feed Management',
    requestedBy: 'Okello James',
    priority: 'high',
    procurementMethod: 'rfq',
    purpose: 'Monthly feed procurement for broiler finisher phase. Current stock critically low.',
    budgetCode: 'BC-FEED-2026',
    costCentre: 'CC-FARM-001',
    estimatedTotal: 25000000,
    currency: 'UGX',
    status: 'pending_approval',
    items: [
      {
        id: 'ri-001',
        description: 'Broiler Finisher Feed',
        category: 'Feed',
        unit: 'bag',
        quantity: 200,
        estimatedUnitCost: 125000,
        estimatedTotal: 25000000,
        preferredSupplier: 'Ugachick Poultry Breeders',
        specifications: '50kg bags, protein ≥18%, energy ≥3100 kcal/kg',
        currentStock: 15,
      },
    ],
    approvals: [
      { id: 'ap-001', level: 1, approverName: 'Nakato Sarah', approverRole: 'Department Head', action: 'approved', comments: 'Stock is critically low. Approved.', date: '2026-09-02', time: '09:15' },
      { id: 'ap-002', level: 2, approverName: 'Mugisha Robert', approverRole: 'Farm Manager', action: 'pending' },
      { id: 'ap-003', level: 3, approverName: 'Kato Finance', approverRole: 'Finance/Budget', action: 'pending' },
    ],
    notes: 'Urgent — broiler flock at 4 weeks, finisher phase critical.',
    createdAt: '2026-09-01T08:00:00Z',
    updatedAt: '2026-09-02T09:15:00Z',
  },
  {
    id: 'req-002',
    requisitionNumber: 'PR-2026-0044',
    requestDate: '2026-08-28',
    requiredByDate: '2026-09-05',
    farm: 'Idudi Poultry Farm',
    department: 'Health & Veterinary',
    requestedBy: 'Dr. Apio Grace',
    priority: 'urgent',
    procurementMethod: 'direct',
    purpose: 'Newcastle Disease vaccine for flock vaccination scheduled 5 Sep 2026.',
    budgetCode: 'BC-HEALTH-2026',
    costCentre: 'CC-FARM-001',
    estimatedTotal: 3600000,
    currency: 'UGX',
    status: 'approved',
    items: [
      {
        id: 'ri-002',
        description: 'Newcastle Disease Vaccine (La Sota)',
        category: 'Vaccines',
        unit: 'dose',
        quantity: 3000,
        estimatedUnitCost: 1200,
        estimatedTotal: 3600000,
        preferredSupplier: 'Kampala Vet Supplies',
        specifications: 'Live attenuated, cold chain required, expiry ≥6 months',
        currentStock: 0,
      },
    ],
    approvals: [
      { id: 'ap-004', level: 1, approverName: 'Nakato Sarah', approverRole: 'Department Head', action: 'approved', date: '2026-08-29', time: '10:00' },
      { id: 'ap-005', level: 2, approverName: 'Mugisha Robert', approverRole: 'Farm Manager', action: 'approved', date: '2026-08-29', time: '14:30' },
    ],
    createdAt: '2026-08-28T07:30:00Z',
    updatedAt: '2026-08-29T14:30:00Z',
  },
  {
    id: 'req-003',
    requisitionNumber: 'PR-2026-0043',
    requestDate: '2026-08-25',
    requiredByDate: '2026-09-01',
    farm: 'Idudi Poultry Farm',
    department: 'Farm Operations',
    requestedBy: 'Waiswa Peter',
    priority: 'normal',
    procurementMethod: 'rfq',
    purpose: 'Replacement of worn-out PPE for farm workers.',
    budgetCode: 'BC-OPS-2026',
    costCentre: 'CC-FARM-001',
    estimatedTotal: 1850000,
    currency: 'UGX',
    status: 'converted_po',
    items: [
      { id: 'ri-003', description: 'Protective Overalls', category: 'PPE', unit: 'piece', quantity: 20, estimatedUnitCost: 45000, estimatedTotal: 900000 },
      { id: 'ri-004', description: 'Rubber Boots (pairs)', category: 'PPE', unit: 'pair', quantity: 20, estimatedUnitCost: 35000, estimatedTotal: 700000 },
      { id: 'ri-005', description: 'Face Masks (box)', category: 'PPE', unit: 'box', quantity: 10, estimatedUnitCost: 25000, estimatedTotal: 250000 },
    ],
    approvals: [
      { id: 'ap-006', level: 1, approverName: 'Nakato Sarah', approverRole: 'Department Head', action: 'approved', date: '2026-08-26', time: '08:00' },
      { id: 'ap-007', level: 2, approverName: 'Mugisha Robert', approverRole: 'Farm Manager', action: 'approved', date: '2026-08-26', time: '11:00' },
    ],
    createdAt: '2026-08-25T09:00:00Z',
    updatedAt: '2026-08-26T11:00:00Z',
  },
  {
    id: 'req-004',
    requisitionNumber: 'PR-2026-0042',
    requestDate: '2026-08-20',
    requiredByDate: '2026-08-30',
    farm: 'Idudi Poultry Farm',
    department: 'Administration',
    requestedBy: 'Namukasa Rita',
    priority: 'low',
    procurementMethod: 'petty',
    purpose: 'Office supplies for administrative use.',
    budgetCode: 'BC-ADMIN-2026',
    costCentre: 'CC-ADMIN-001',
    estimatedTotal: 450000,
    currency: 'UGX',
    status: 'completed',
    items: [
      { id: 'ri-006', description: 'A4 Paper (reams)', category: 'Office Supplies', unit: 'ream', quantity: 10, estimatedUnitCost: 25000, estimatedTotal: 250000 },
      { id: 'ri-007', description: 'Pens and stationery', category: 'Office Supplies', unit: 'set', quantity: 5, estimatedUnitCost: 40000, estimatedTotal: 200000 },
    ],
    approvals: [
      { id: 'ap-008', level: 1, approverName: 'Mugisha Robert', approverRole: 'Farm Manager', action: 'approved', date: '2026-08-21', time: '09:00' },
    ],
    createdAt: '2026-08-20T10:00:00Z',
    updatedAt: '2026-08-21T09:00:00Z',
  },
  {
    id: 'req-005',
    requisitionNumber: 'PR-2026-0046',
    requestDate: '2026-09-03',
    requiredByDate: '2026-09-15',
    farm: 'Idudi Poultry Farm',
    department: 'Maintenance',
    requestedBy: 'Ssemakula John',
    priority: 'normal',
    procurementMethod: 'rfq',
    purpose: 'Repair of water pump and replacement of spare parts.',
    budgetCode: 'BC-MAINT-2026',
    costCentre: 'CC-FARM-001',
    estimatedTotal: 2800000,
    currency: 'UGX',
    status: 'draft',
    items: [
      { id: 'ri-008', description: 'Water Pump Motor', category: 'Spare Parts', unit: 'piece', quantity: 1, estimatedUnitCost: 1800000, estimatedTotal: 1800000 },
      { id: 'ri-009', description: 'PVC Pipes (20mm)', category: 'Spare Parts', unit: 'piece', quantity: 20, estimatedUnitCost: 15000, estimatedTotal: 300000 },
      { id: 'ri-010', description: 'Pipe Fittings', category: 'Spare Parts', unit: 'set', quantity: 2, estimatedUnitCost: 350000, estimatedTotal: 700000 },
    ],
    approvals: [],
    createdAt: '2026-09-03T11:00:00Z',
    updatedAt: '2026-09-03T11:00:00Z',
  },
];

export const MOCK_PURCHASE_ORDERS: PurchaseOrder[] = [
  {
    id: 'po-001',
    poNumber: 'PO-2026-0123',
    purchaseDate: '2026-08-30',
    supplier: 'Ugachick Poultry Breeders',
    supplierCode: 'UPB-001',
    farm: 'Idudi Poultry Farm',
    department: 'Feed Management',
    requisitionId: 'req-003',
    requisitionNumber: 'PR-2026-0043',
    buyer: 'Mugisha Robert',
    currency: 'UGX',
    paymentTerms: 'Net 30',
    deliveryTerms: 'DDP',
    expectedDeliveryDate: '2026-09-05',
    deliveryAddress: 'Idudi Poultry Farm, Iganga Road, Idudi',
    subtotal: 1850000,
    discount: 92500,
    tax: 0,
    deliveryCharges: 50000,
    grandTotal: 1807500,
    status: 'fully_delivered',
    items: [
      { id: 'poi-001', description: 'Protective Overalls', category: 'PPE', unit: 'piece', quantity: 20, unitPrice: 45000, tax: 0, discount: 5, lineTotal: 855000 },
      { id: 'poi-002', description: 'Rubber Boots (pairs)', category: 'PPE', unit: 'pair', quantity: 20, unitPrice: 35000, tax: 0, discount: 5, lineTotal: 665000 },
      { id: 'poi-003', description: 'Face Masks (box)', category: 'PPE', unit: 'box', quantity: 10, unitPrice: 25000, tax: 0, discount: 5, lineTotal: 237500 },
    ],
    notes: 'Delivery to main store. Contact Waiswa Peter on arrival.',
    createdAt: '2026-08-30T10:00:00Z',
    updatedAt: '2026-09-05T14:00:00Z',
  },
  {
    id: 'po-002',
    poNumber: 'PO-2026-0124',
    purchaseDate: '2026-08-30',
    supplier: 'Kampala Vet Supplies',
    supplierCode: 'KVS-003',
    farm: 'Idudi Poultry Farm',
    department: 'Health & Veterinary',
    requisitionId: 'req-002',
    requisitionNumber: 'PR-2026-0044',
    buyer: 'Mugisha Robert',
    currency: 'UGX',
    paymentTerms: 'Net 14',
    deliveryTerms: 'CIF',
    expectedDeliveryDate: '2026-09-04',
    deliveryAddress: 'Idudi Poultry Farm, Iganga Road, Idudi',
    subtotal: 3600000,
    discount: 0,
    tax: 0,
    deliveryCharges: 0,
    grandTotal: 3600000,
    status: 'fully_delivered',
    items: [
      { id: 'poi-004', description: 'Newcastle Disease Vaccine (La Sota)', category: 'Vaccines', unit: 'dose', quantity: 3000, unitPrice: 1200, tax: 0, discount: 0, lineTotal: 3600000 },
    ],
    notes: 'Cold chain delivery required. Refrigerated transport mandatory.',
    createdAt: '2026-08-30T11:00:00Z',
    updatedAt: '2026-09-04T09:00:00Z',
  },
  {
    id: 'po-003',
    poNumber: 'PO-2026-0125',
    purchaseDate: '2026-09-02',
    supplier: 'Ugachick Poultry Breeders',
    supplierCode: 'UPB-001',
    farm: 'Idudi Poultry Farm',
    department: 'Feed Management',
    requisitionId: 'req-001',
    requisitionNumber: 'PR-2026-0045',
    buyer: 'Mugisha Robert',
    currency: 'UGX',
    paymentTerms: 'Net 30',
    deliveryTerms: 'DDP',
    expectedDeliveryDate: '2026-09-08',
    deliveryAddress: 'Idudi Poultry Farm, Iganga Road, Idudi',
    subtotal: 25000000,
    discount: 500000,
    tax: 0,
    deliveryCharges: 200000,
    grandTotal: 24700000,
    status: 'partially_delivered',
    items: [
      { id: 'poi-005', description: 'Broiler Finisher Feed (50kg bags)', category: 'Feed', unit: 'bag', quantity: 200, unitPrice: 125000, tax: 0, discount: 2, lineTotal: 24500000 },
    ],
    notes: 'Partial delivery accepted. Remaining 80 bags expected by 12 Sep 2026.',
    createdAt: '2026-09-02T09:00:00Z',
    updatedAt: '2026-09-05T16:00:00Z',
  },
  {
    id: 'po-004',
    poNumber: 'PO-2026-0126',
    purchaseDate: '2026-09-04',
    supplier: 'East Africa Agri-Inputs',
    supplierCode: 'EAA-004',
    farm: 'Idudi Poultry Farm',
    department: 'Farm Operations',
    buyer: 'Mugisha Robert',
    currency: 'UGX',
    paymentTerms: 'Cash on Delivery',
    deliveryTerms: 'EXW',
    expectedDeliveryDate: '2026-09-10',
    deliveryAddress: 'Idudi Poultry Farm, Iganga Road, Idudi',
    subtotal: 1200000,
    discount: 0,
    tax: 0,
    deliveryCharges: 80000,
    grandTotal: 1280000,
    status: 'approved',
    items: [
      { id: 'poi-006', description: 'Disinfectant (Virkon S)', category: 'Disinfectants', unit: 'kg', quantity: 20, unitPrice: 45000, tax: 0, discount: 0, lineTotal: 900000 },
      { id: 'poi-007', description: 'Fumigation Chemicals', category: 'Chemicals', unit: 'litre', quantity: 10, unitPrice: 30000, tax: 0, discount: 0, lineTotal: 300000 },
    ],
    notes: 'Biosecurity restocking.',
    createdAt: '2026-09-04T08:00:00Z',
    updatedAt: '2026-09-04T08:00:00Z',
  },
];

export const MOCK_GRNS: GoodsReceivedNote[] = [
  {
    id: 'grn-001',
    grnNumber: 'GRN-2026-0032',
    poNumber: 'PO-2026-0123',
    poId: 'po-001',
    supplier: 'Ugachick Poultry Breeders',
    deliveryNoteNumber: 'DN-UPB-4521',
    receiptDate: '2026-09-05',
    farm: 'Idudi Poultry Farm',
    store: 'Main Store',
    receivedBy: 'Waiswa Peter',
    inspectedBy: 'Nakato Sarah',
    vehicleRef: 'UAX 234K',
    status: 'approved',
    items: [
      { id: 'gi-001', description: 'Protective Overalls', unit: 'piece', orderedQty: 20, previouslyReceived: 0, deliveredQty: 20, acceptedQty: 20, rejectedQty: 0, damagedQty: 0, remarks: 'All in good condition' },
      { id: 'gi-002', description: 'Rubber Boots (pairs)', unit: 'pair', orderedQty: 20, previouslyReceived: 0, deliveredQty: 20, acceptedQty: 19, rejectedQty: 1, damagedQty: 1, remarks: '1 pair damaged in transit' },
      { id: 'gi-003', description: 'Face Masks (box)', unit: 'box', orderedQty: 10, previouslyReceived: 0, deliveredQty: 10, acceptedQty: 10, rejectedQty: 0, damagedQty: 0 },
    ],
    notes: 'Minor damage on 1 pair of boots. Supplier notified for replacement.',
    createdAt: '2026-09-05T14:00:00Z',
  },
  {
    id: 'grn-002',
    grnNumber: 'GRN-2026-0033',
    poNumber: 'PO-2026-0124',
    poId: 'po-002',
    supplier: 'Kampala Vet Supplies',
    deliveryNoteNumber: 'DN-KVS-0891',
    receiptDate: '2026-09-04',
    farm: 'Idudi Poultry Farm',
    store: 'Veterinary Store',
    receivedBy: 'Dr. Apio Grace',
    inspectedBy: 'Dr. Apio Grace',
    vehicleRef: 'UBE 567M',
    status: 'approved',
    items: [
      { id: 'gi-004', description: 'Newcastle Disease Vaccine (La Sota)', unit: 'dose', orderedQty: 3000, previouslyReceived: 0, deliveredQty: 3000, acceptedQty: 3000, rejectedQty: 0, damagedQty: 0, batchNumber: 'BATCH-ND-2026-09', expiryDate: '2027-03-01', remarks: 'Cold chain maintained throughout' },
    ],
    notes: 'Vaccine received in good condition. Cold chain verified.',
    createdAt: '2026-09-04T09:00:00Z',
  },
  {
    id: 'grn-003',
    grnNumber: 'GRN-2026-0034',
    poNumber: 'PO-2026-0125',
    poId: 'po-003',
    supplier: 'Ugachick Poultry Breeders',
    deliveryNoteNumber: 'DN-UPB-4589',
    receiptDate: '2026-09-05',
    farm: 'Idudi Poultry Farm',
    store: 'Feed Store',
    receivedBy: 'Ssemakula John',
    inspectedBy: 'Nakato Sarah',
    vehicleRef: 'UAK 890B',
    status: 'approved',
    items: [
      { id: 'gi-005', description: 'Broiler Finisher Feed (50kg bags)', unit: 'bag', orderedQty: 200, previouslyReceived: 0, deliveredQty: 120, acceptedQty: 120, rejectedQty: 0, damagedQty: 0, batchNumber: 'BF-2026-09-A', remarks: 'Partial delivery. Remaining 80 bags expected 12 Sep.' },
    ],
    notes: 'First partial delivery of 120 bags. PO remains open for remaining 80 bags.',
    createdAt: '2026-09-05T16:00:00Z',
  },
];

export const MOCK_INVOICES: SupplierInvoice[] = [
  {
    id: 'inv-001',
    invoiceNumber: 'INV-UPB-20260905',
    supplier: 'Ugachick Poultry Breeders',
    poNumber: 'PO-2026-0123',
    poId: 'po-001',
    grnNumber: 'GRN-2026-0032',
    invoiceDate: '2026-09-05',
    dueDate: '2026-10-05',
    currency: 'UGX',
    subtotal: 1850000,
    tax: 0,
    discount: 92500,
    total: 1807500,
    status: 'matched',
    matchStatus: 'matched',
    paymentStatus: 'unpaid',
    notes: 'Invoice matches PO and GRN.',
    createdAt: '2026-09-05T15:00:00Z',
  },
  {
    id: 'inv-002',
    invoiceNumber: 'INV-KVS-20260904',
    supplier: 'Kampala Vet Supplies',
    poNumber: 'PO-2026-0124',
    poId: 'po-002',
    grnNumber: 'GRN-2026-0033',
    invoiceDate: '2026-09-04',
    dueDate: '2026-09-18',
    currency: 'UGX',
    subtotal: 3600000,
    tax: 0,
    discount: 0,
    total: 3600000,
    status: 'approved',
    matchStatus: 'matched',
    paymentStatus: 'paid',
    createdAt: '2026-09-04T10:00:00Z',
  },
  {
    id: 'inv-003',
    invoiceNumber: 'INV-UPB-20260906',
    supplier: 'Ugachick Poultry Breeders',
    poNumber: 'PO-2026-0125',
    poId: 'po-003',
    grnNumber: 'GRN-2026-0034',
    invoiceDate: '2026-09-06',
    dueDate: '2026-10-06',
    currency: 'UGX',
    subtotal: 15000000,
    tax: 0,
    discount: 300000,
    total: 14700000,
    status: 'minor_variance',
    matchStatus: 'minor_variance',
    paymentStatus: 'unpaid',
    notes: 'Invoice for partial delivery (120 bags). Minor price variance of UGX 50,000.',
    createdAt: '2026-09-06T08:00:00Z',
  },
];

export const MOCK_ALERTS: ProcurementAlert[] = [
  { id: 'alert-001', type: 'approval_needed', title: 'PR-2026-0045 Awaiting Approval', description: 'Feed requisition for 200 bags of Broiler Finisher Feed requires your approval.', severity: 'warning', date: '2026-09-02', actionUrl: '/procurement/approvals' },
  { id: 'alert-002', type: 'delivery_overdue', title: 'PO-2026-0126 Delivery Overdue', description: 'Expected delivery from East Africa Agri-Inputs was due 10 Sep 2026. Now 5 days overdue.', severity: 'danger', date: '2026-09-10', actionUrl: '/procurement/purchase-orders' },
  { id: 'alert-003', type: 'invoice_mismatch', title: 'Invoice Variance Detected', description: 'INV-UPB-20260906 has a minor price variance of UGX 50,000 vs PO-2026-0125.', severity: 'warning', date: '2026-09-06', actionUrl: '/procurement/invoices' },
  { id: 'alert-004', type: 'approval_needed', title: 'PR-2026-0046 Pending Submission', description: 'Maintenance requisition for water pump repair is still in Draft status.', severity: 'info', date: '2026-09-03', actionUrl: '/procurement/requisitions' },
  { id: 'alert-005', type: 'rfq_deadline', title: 'RFQ-2026-0012 Deadline Approaching', description: 'Submission deadline for Broiler Finisher Feed RFQ is in 2 days (Sep 7, 2026).', severity: 'warning', date: '2026-09-05', actionUrl: '/procurement/rfqs' },
];

// ─── Mock RFQs ────────────────────────────────────────────────────────────────

export const MOCK_RFQS: RFQ[] = [
  {
    id: 'rfq-001',
    rfqNumber: 'RFQ-2026-0012',
    rfqDate: '2026-09-03',
    submissionDeadline: '2026-09-07',
    farm: 'Idudi Poultry Farm',
    requisitionId: 'req-001',
    requisitionNumber: 'PR-2026-0045',
    procurementOfficer: 'Mugisha Robert',
    currency: 'UGX',
    deliveryLocation: 'Idudi Poultry Farm, Iganga Road, Idudi',
    paymentTerms: 'Net 30',
    deliveryTerms: 'DDP',
    notes: 'Urgent requirement. Suppliers must confirm cold chain capability for feed quality.',
    status: 'open',
    suppliers: [
      { supplierId: 'sup-001', supplierName: 'Ugachick Poultry Breeders', supplierCode: 'UPB-001', sentDate: '2026-09-03', responseStatus: 'responded' },
      { supplierId: 'sup-002', supplierName: 'Nile Agro Supplies Ltd', supplierCode: 'NAS-002', sentDate: '2026-09-03', responseStatus: 'responded' },
      { supplierId: 'sup-004', supplierName: 'East Africa Agri-Inputs', supplierCode: 'EAA-004', sentDate: '2026-09-03', responseStatus: 'pending' },
    ],
    items: [
      { id: 'ri-001', description: 'Broiler Finisher Feed (50kg bags)', specifications: 'Protein ≥18%, energy ≥3100 kcal/kg, 50kg bags', category: 'Feed', unit: 'bag', quantity: 200, requiredDeliveryDate: '2026-09-10' },
    ],
    createdAt: '2026-09-03T08:00:00Z',
    updatedAt: '2026-09-03T08:00:00Z',
  },
  {
    id: 'rfq-002',
    rfqNumber: 'RFQ-2026-0011',
    rfqDate: '2026-08-25',
    submissionDeadline: '2026-08-30',
    farm: 'Idudi Poultry Farm',
    requisitionId: 'req-005',
    requisitionNumber: 'PR-2026-0046',
    procurementOfficer: 'Mugisha Robert',
    currency: 'UGX',
    deliveryLocation: 'Idudi Poultry Farm, Iganga Road, Idudi',
    paymentTerms: 'Net 14',
    deliveryTerms: 'EXW',
    notes: 'Spare parts for water pump repair. Suppliers must provide warranty.',
    status: 'closed',
    suppliers: [
      { supplierId: 'sup-005', supplierName: 'Jinja Farm Supplies', supplierCode: 'JFS-005', sentDate: '2026-08-25', responseStatus: 'responded' },
      { supplierId: 'sup-004', supplierName: 'East Africa Agri-Inputs', supplierCode: 'EAA-004', sentDate: '2026-08-25', responseStatus: 'responded' },
    ],
    items: [
      { id: 'ri-008', description: 'Water Pump Motor', specifications: '1.5HP, 220V, compatible with Grundfos CM5', category: 'Spare Parts', unit: 'piece', quantity: 1, requiredDeliveryDate: '2026-09-01' },
      { id: 'ri-009', description: 'PVC Pipes (20mm)', specifications: '20mm diameter, 6m length, pressure rated', category: 'Spare Parts', unit: 'piece', quantity: 20, requiredDeliveryDate: '2026-09-01' },
    ],
    createdAt: '2026-08-25T09:00:00Z',
    updatedAt: '2026-08-30T17:00:00Z',
  },
  {
    id: 'rfq-003',
    rfqNumber: 'RFQ-2026-0010',
    rfqDate: '2026-08-15',
    submissionDeadline: '2026-08-20',
    farm: 'Idudi Poultry Farm',
    procurementOfficer: 'Nakato Sarah',
    currency: 'UGX',
    deliveryLocation: 'Idudi Poultry Farm, Iganga Road, Idudi',
    paymentTerms: 'Cash on Delivery',
    deliveryTerms: 'DDP',
    status: 'evaluated',
    suppliers: [
      { supplierId: 'sup-003', supplierName: 'Kampala Vet Supplies', supplierCode: 'KVS-003', sentDate: '2026-08-15', responseStatus: 'responded' },
      { supplierId: 'sup-004', supplierName: 'East Africa Agri-Inputs', supplierCode: 'EAA-004', sentDate: '2026-08-15', responseStatus: 'declined' },
    ],
    items: [
      { id: 'ri-011', description: 'Disinfectant (Virkon S)', specifications: '1kg sachets, broad spectrum', category: 'Disinfectants', unit: 'kg', quantity: 20 },
    ],
    createdAt: '2026-08-15T10:00:00Z',
    updatedAt: '2026-08-22T14:00:00Z',
  },
  {
    id: 'rfq-004',
    rfqNumber: 'RFQ-2026-0013',
    rfqDate: '2026-09-05',
    submissionDeadline: '2026-09-12',
    farm: 'Idudi Poultry Farm',
    procurementOfficer: 'Mugisha Robert',
    currency: 'UGX',
    deliveryLocation: 'Idudi Poultry Farm, Iganga Road, Idudi',
    paymentTerms: 'Net 30',
    deliveryTerms: 'DDP',
    notes: 'Annual PPE restocking for all farm workers.',
    status: 'draft',
    suppliers: [],
    items: [
      { id: 'ri-012', description: 'Protective Overalls', specifications: 'Size M-XL, chemical resistant', category: 'PPE', unit: 'piece', quantity: 30 },
      { id: 'ri-013', description: 'Safety Boots', specifications: 'Steel toe cap, waterproof', category: 'PPE', unit: 'pair', quantity: 30 },
    ],
    createdAt: '2026-09-05T11:00:00Z',
    updatedAt: '2026-09-05T11:00:00Z',
  },
];

// ─── Mock Quotations ──────────────────────────────────────────────────────────

export const MOCK_QUOTATIONS: Quotation[] = [
  {
    id: 'quot-001',
    quotationNumber: 'QT-UPB-2026-0045',
    supplierId: 'sup-001',
    supplierName: 'Ugachick Poultry Breeders',
    rfqId: 'rfq-001',
    rfqNumber: 'RFQ-2026-0012',
    quotationDate: '2026-09-04',
    validityDate: '2026-09-18',
    currency: 'UGX',
    paymentTerms: 'Net 30',
    deliveryPeriod: '3 days',
    deliveryCharges: 200000,
    tax: 0,
    discount: 500000,
    subtotal: 25000000,
    grandTotal: 24700000,
    warranty: 'N/A',
    notes: 'We can deliver within 3 working days. Bulk discount of UGX 500,000 applied.',
    status: 'shortlisted',
    items: [
      {
        id: 'qi-001',
        description: 'Broiler Finisher Feed (50kg bags)',
        unit: 'bag',
        quantity: 200,
        unitPrice: 125000,
        discount: 2,
        tax: 0,
        totalPrice: 24500000,
        deliveryTime: '3 days',
        brand: 'Ugachick Premium',
        specificationsCompliance: 'full',
      },
    ],
    createdAt: '2026-09-04T10:00:00Z',
    updatedAt: '2026-09-04T10:00:00Z',
  },
  {
    id: 'quot-002',
    quotationNumber: 'QT-NAS-2026-0031',
    supplierId: 'sup-002',
    supplierName: 'Nile Agro Supplies Ltd',
    rfqId: 'rfq-001',
    rfqNumber: 'RFQ-2026-0012',
    quotationDate: '2026-09-05',
    validityDate: '2026-09-19',
    currency: 'UGX',
    paymentTerms: 'Net 14',
    deliveryPeriod: '5 days',
    deliveryCharges: 150000,
    tax: 0,
    discount: 0,
    subtotal: 24000000,
    grandTotal: 24150000,
    warranty: 'N/A',
    notes: 'Competitive pricing. Delivery in 5 working days.',
    status: 'received',
    items: [
      {
        id: 'qi-002',
        description: 'Broiler Finisher Feed (50kg bags)',
        unit: 'bag',
        quantity: 200,
        unitPrice: 120000,
        discount: 0,
        tax: 0,
        totalPrice: 24000000,
        deliveryTime: '5 days',
        brand: 'Nile Gold Feed',
        specificationsCompliance: 'partial',
      },
    ],
    createdAt: '2026-09-05T09:00:00Z',
    updatedAt: '2026-09-05T09:00:00Z',
  },
  {
    id: 'quot-003',
    quotationNumber: 'QT-JFS-2026-0018',
    supplierId: 'sup-005',
    supplierName: 'Jinja Farm Supplies',
    rfqId: 'rfq-002',
    rfqNumber: 'RFQ-2026-0011',
    quotationDate: '2026-08-27',
    validityDate: '2026-09-10',
    currency: 'UGX',
    paymentTerms: 'Net 14',
    deliveryPeriod: '2 days',
    deliveryCharges: 50000,
    tax: 0,
    discount: 0,
    subtotal: 2100000,
    grandTotal: 2150000,
    warranty: '6 months on motor',
    notes: 'Genuine spare parts. Motor comes with 6-month warranty.',
    status: 'accepted',
    items: [
      { id: 'qi-003', description: 'Water Pump Motor', unit: 'piece', quantity: 1, unitPrice: 1800000, discount: 0, tax: 0, totalPrice: 1800000, deliveryTime: '2 days', brand: 'Grundfos', specificationsCompliance: 'full' },
      { id: 'qi-004', description: 'PVC Pipes (20mm)', unit: 'piece', quantity: 20, unitPrice: 15000, discount: 0, tax: 0, totalPrice: 300000, deliveryTime: '2 days', brand: 'Wavin', specificationsCompliance: 'full' },
    ],
    createdAt: '2026-08-27T11:00:00Z',
    updatedAt: '2026-08-28T09:00:00Z',
  },
  {
    id: 'quot-004',
    quotationNumber: 'QT-EAA-2026-0022',
    supplierId: 'sup-004',
    supplierName: 'East Africa Agri-Inputs',
    rfqId: 'rfq-002',
    rfqNumber: 'RFQ-2026-0011',
    quotationDate: '2026-08-28',
    validityDate: '2026-09-11',
    currency: 'UGX',
    paymentTerms: 'Cash on Delivery',
    deliveryPeriod: '4 days',
    deliveryCharges: 80000,
    tax: 0,
    discount: 0,
    subtotal: 2350000,
    grandTotal: 2430000,
    warranty: '3 months on motor',
    notes: 'Alternative motor brand available. Slightly higher price.',
    status: 'rejected',
    items: [
      { id: 'qi-005', description: 'Water Pump Motor', unit: 'piece', quantity: 1, unitPrice: 2050000, discount: 0, tax: 0, totalPrice: 2050000, deliveryTime: '4 days', brand: 'Pedrollo', specificationsCompliance: 'partial' },
      { id: 'qi-006', description: 'PVC Pipes (20mm)', unit: 'piece', quantity: 20, unitPrice: 15000, discount: 0, tax: 0, totalPrice: 300000, deliveryTime: '4 days', brand: 'Generic', specificationsCompliance: 'full' },
    ],
    createdAt: '2026-08-28T14:00:00Z',
    updatedAt: '2026-08-29T10:00:00Z',
  },
];

// ─── Status Styles ────────────────────────────────────────────────────────────

export const REQUISITION_STATUS_STYLES: Record<RequisitionStatus, { label: string; bg: string; text: string; dot: string }> = {
  draft: { label: 'Draft', bg: 'bg-gray-100', text: 'text-gray-600', dot: 'bg-gray-400' },
  submitted: { label: 'Submitted', bg: 'bg-blue-50', text: 'text-blue-700', dot: 'bg-blue-500' },
  pending_approval: { label: 'Pending Approval', bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
  approved: { label: 'Approved', bg: 'bg-green-50', text: 'text-green-700', dot: 'bg-green-500' },
  partially_approved: { label: 'Partially Approved', bg: 'bg-teal-50', text: 'text-teal-700', dot: 'bg-teal-500' },
  rejected: { label: 'Rejected', bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-500' },
  returned: { label: 'Returned', bg: 'bg-orange-50', text: 'text-orange-700', dot: 'bg-orange-500' },
  converted_rfq: { label: 'Converted to RFQ', bg: 'bg-purple-50', text: 'text-purple-700', dot: 'bg-purple-500' },
  converted_po: { label: 'Converted to PO', bg: 'bg-indigo-50', text: 'text-indigo-700', dot: 'bg-indigo-500' },
  partially_procured: { label: 'Partially Procured', bg: 'bg-cyan-50', text: 'text-cyan-700', dot: 'bg-cyan-500' },
  completed: { label: 'Completed', bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  cancelled: { label: 'Cancelled', bg: 'bg-gray-100', text: 'text-gray-500', dot: 'bg-gray-400' },
};

export const PO_STATUS_STYLES: Record<POStatus, { label: string; bg: string; text: string; dot: string }> = {
  draft: { label: 'Draft', bg: 'bg-gray-100', text: 'text-gray-600', dot: 'bg-gray-400' },
  pending_approval: { label: 'Pending Approval', bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
  approved: { label: 'Approved', bg: 'bg-green-50', text: 'text-green-700', dot: 'bg-green-500' },
  issued: { label: 'Issued', bg: 'bg-blue-50', text: 'text-blue-700', dot: 'bg-blue-500' },
  partially_delivered: { label: 'Partially Delivered', bg: 'bg-orange-50', text: 'text-orange-700', dot: 'bg-orange-500' },
  fully_delivered: { label: 'Fully Delivered', bg: 'bg-teal-50', text: 'text-teal-700', dot: 'bg-teal-500' },
  closed: { label: 'Closed', bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  cancelled: { label: 'Cancelled', bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-400' },
};

export const GRN_STATUS_STYLES: Record<GRNStatus, { label: string; bg: string; text: string }> = {
  draft: { label: 'Draft', bg: 'bg-gray-100', text: 'text-gray-600' },
  pending_inspection: { label: 'Pending Inspection', bg: 'bg-amber-50', text: 'text-amber-700' },
  approved: { label: 'Approved', bg: 'bg-green-50', text: 'text-green-700' },
  rejected: { label: 'Rejected', bg: 'bg-red-50', text: 'text-red-700' },
  partial: { label: 'Partial', bg: 'bg-orange-50', text: 'text-orange-700' },
};

export const RFQ_STATUS_STYLES: Record<RFQStatus, { label: string; bg: string; text: string; dot: string }> = {
  draft: { label: 'Draft', bg: 'bg-gray-100', text: 'text-gray-600', dot: 'bg-gray-400' },
  issued: { label: 'Issued', bg: 'bg-blue-50', text: 'text-blue-700', dot: 'bg-blue-500' },
  open: { label: 'Open', bg: 'bg-green-50', text: 'text-green-700', dot: 'bg-green-500' },
  closed: { label: 'Closed', bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
  evaluated: { label: 'Evaluated', bg: 'bg-purple-50', text: 'text-purple-700', dot: 'bg-purple-500' },
  cancelled: { label: 'Cancelled', bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-400' },
};

export const QUOTATION_STATUS_STYLES: Record<QuotationStatus, { label: string; bg: string; text: string; dot: string }> = {
  received: { label: 'Received', bg: 'bg-blue-50', text: 'text-blue-700', dot: 'bg-blue-500' },
  under_review: { label: 'Under Review', bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
  shortlisted: { label: 'Shortlisted', bg: 'bg-teal-50', text: 'text-teal-700', dot: 'bg-teal-500' },
  accepted: { label: 'Accepted', bg: 'bg-green-50', text: 'text-green-700', dot: 'bg-green-500' },
  rejected: { label: 'Rejected', bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-400' },
};

export const PRIORITY_STYLES: Record<Priority, { label: string; bg: string; text: string }> = {
  low: { label: 'Low', bg: 'bg-gray-100', text: 'text-gray-500' },
  normal: { label: 'Normal', bg: 'bg-blue-50', text: 'text-blue-600' },
  high: { label: 'High', bg: 'bg-amber-50', text: 'text-amber-700' },
  urgent: { label: 'Urgent', bg: 'bg-orange-50', text: 'text-orange-700' },
  emergency: { label: 'Emergency', bg: 'bg-red-100', text: 'text-red-700' },
};

export const PROCUREMENT_METHOD_LABELS: Record<ProcurementMethod, string> = {
  rfq: 'Request for Quotation',
  direct: 'Direct Purchase',
  competitive_bidding: 'Competitive Bidding',
  single_source: 'Single Source',
  emergency: 'Emergency Purchase',
  framework: 'Framework Agreement',
  petty: 'Petty Purchase',
};

export function formatUGX(amount: number): string {
  return 'UGX ' + amount.toLocaleString('en-UG');
}
